// BUG-473: the order API's limits (webshop OrderLineRequest.MAX_QUANTITY, CreateOrderRequest.MAX_LINES).
// An order with more than 20 of one item or more than 30 lines is refused with a 400, so the shop
// never lets the basket get there, and checkout explains it if the server refuses anyway.

import { ApiError } from './shopApi'

/** At most this many of one product (or variant) per order. */
export const MAX_PER_LINE = 20
/** At most this many different products (lines) per order. */
export const MAX_LINES = 30

/**
 * Why "add one more" is not possible: none in stock ('stock'), the basket already holds all we have
 * ('inCart', or 'lastInCart' when that is the last one), the per-line cap, or the line cap.
 */
export type AddBlock = 'stock' | 'inCart' | 'lastInCart' | 'perLine' | 'lines'

/**
 * Whether one more of a product fits: `inCart` is what its line already holds (0 = no line yet),
 * `available` the stock, `lineCount` the basket's number of lines. Null when the add can go ahead.
 */
export function addBlock(inCart: number, available: number, lineCount: number): AddBlock | null {
  if (inCart >= MAX_PER_LINE) return 'perLine'
  if (available <= 0) return 'stock'
  // BUG-520: stock is left, but the shopper's basket holds all of it; say so instead of "none left".
  if (inCart >= available) return available === 1 ? 'lastInCart' : 'inCart'
  if (inCart === 0 && lineCount >= MAX_LINES) return 'lines'
  return null
}

/** The translation key and values for a block's short hint. */
export function blockHint(block: AddBlock): { key: string; values: { count: number } } {
  if (block === 'perLine') return { key: 'stock.maxPerLine', values: { count: MAX_PER_LINE } }
  if (block === 'lines') return { key: 'stock.maxLines', values: { count: MAX_LINES } }
  if (block === 'inCart') return { key: 'stock.allInCart', values: { count: 0 } }
  if (block === 'lastInCart') return { key: 'stock.lastInCart', values: { count: 0 } }
  return { key: 'stock.noMore', values: { count: 0 } }
}

/** A quantity kept inside the per-line cap. */
export function capQuantity(quantity: number): number {
  return Math.min(quantity, MAX_PER_LINE)
}

/**
 * True when the order API refused the basket for these limits: its validation 400
 * (`title: "Validation failed"`) names `lines` (too many lines) or `lines[n].quantity` (too many of one).
 * Any other error is not ours to explain here.
 */
export function isOrderLimitError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 400) return false
  const errors = (err.problem as { errors?: unknown } | null)?.errors
  if (!errors || typeof errors !== 'object') return false
  return Object.keys(errors).some((field) => /^lines(\[\d+\]\.quantity)?$/.test(field))
}
